import fetch from 'isomorphic-fetch';

export const APPLICATION_UPDATE_PREFETCH_TIMEOUT_MS = 15000;

function withTimeout<T>(promise: Promise<T>, milliseconds: number, controller: AbortController): Promise<T> {
	return new Promise((resolve, reject) => {
		let settled = false;
		const timeout = setTimeout(() => {
			if (settled) {
				return;
			}
			settled = true;
			controller.abort();
			reject(new Error('Application update prefetch timed out'));
		}, milliseconds);

		promise.then(
			(value) => {
				if (settled) {
					return;
				}
				settled = true;
				clearTimeout(timeout);
				resolve(value);
			},
			(error) => {
				if (settled) {
					return;
				}
				settled = true;
				clearTimeout(timeout);
				reject(error);
			}
		);
	});
}

export async function prefetchApplicationDocuments(
	pageUrl: string,
	timeoutMs: number = APPLICATION_UPDATE_PREFETCH_TIMEOUT_MS
): Promise<void> {
	const controller = new AbortController();
	const responses = await withTimeout(
		Promise.all([
			fetch(pageUrl, { cache: 'reload', signal: controller.signal }),
			fetch('/', { cache: 'reload', signal: controller.signal }),
		]),
		timeoutMs,
		controller
	);

	if (responses.some((response) => !response.ok)) {
		controller.abort();
		throw new Error('Application update prefetch failed');
	}

	await withTimeout(Promise.all(responses.map((response) => response.blob())), timeoutMs, controller);
}

export function applyApplicationUpdate(
	dismiss: () => void,
	prefetch: (pageUrl: string) => Promise<void> = prefetchApplicationDocuments,
	reload: () => void = () => window.location.reload()
): Promise<void> {
	return prefetch(window.location.href).then(() => {
		dismiss();
		reload();
	});
}
